import { z } from 'zod';
import { ACCOUNT_STATUSES, USER_ROLES } from '../context';

export type { AccountStatus } from '../context';
// 계정 상태 계약을 함께 내보낸다. (배럴이 이 파일을 export * 하므로 프론트에서도 사용 가능)
export { ACCOUNT_STATUSES } from '../context';

export const MIN_PASSWORD_LENGTH = 10;
export const MAX_PASSWORD_LENGTH = 128;

export const emailSchema = z
  .email({ message: '올바른 이메일 주소를 입력해 주세요.' })
  .max(254)
  .toLowerCase();

export const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `비밀번호는 최소 ${MIN_PASSWORD_LENGTH}자 이상이어야 합니다.`)
  .max(MAX_PASSWORD_LENGTH)
  .regex(/[a-z]/, '소문자를 1자 이상 포함해야 합니다.')
  .regex(/[A-Z]/, '대문자를 1자 이상 포함해야 합니다.')
  .regex(/\d/, '숫자를 1자 이상 포함해야 합니다.');

/**
 * 가입 시 약관 동의. 필수 3종은 true 리터럴이라 동의 없이는 스키마를 통과할 수 없다 —
 * 프론트 체크박스 상태가 그대로 검증되고, 서버는 user_agreements 에 증빙을 남긴다.
 */
export const signUpAgreementsSchema = z.object({
  termsOfService: z.literal(true, { message: '이용약관 동의는 필수입니다.' }),
  privacyPolicy: z.literal(true, { message: '개인정보 처리방침 동의는 필수입니다.' }),
  ageOver19: z.literal(true, { message: '만 19세 이상만 가입할 수 있습니다.' }),
  marketingEmail: z.boolean(),
  marketingSms: z.boolean(),
});

export type SignUpAgreements = z.infer<typeof signUpAgreementsSchema>;

export const signUpInputSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  name: z.string().trim().min(1, '이름을 입력해 주세요.').max(50),
  agreements: signUpAgreementsSchema,
});

export type SignUpInput = z.infer<typeof signUpInputSchema>;

export const signInInputSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, '비밀번호를 입력해 주세요.'),
});

export type SignInInput = z.infer<typeof signInInputSchema>;

export const refreshTokenInputSchema = z.object({
  refreshToken: z.string().min(1),
});

export const verifyEmailInputSchema = z.object({
  token: z.string().min(1),
});

// 인증을 마친 계정의 이메일 변경 요청. 비밀번호 계정만 현재 비밀번호를 함께 보낸다.
export const requestEmailChangeInputSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).optional(),
});

export type RequestEmailChangeInput = z.infer<typeof requestEmailChangeInputSchema>;

// 새 주소로 간 메일 링크의 토큰으로 이메일 변경을 확정한다.
export const confirmEmailChangeInputSchema = z.object({
  token: z.string().min(1),
});

export type ConfirmEmailChangeInput = z.infer<typeof confirmEmailChangeInputSchema>;

// 비밀번호 재설정 요청. 응답은 계정 존재 여부와 무관하게 항상 동일하다. (계정 열거 방지)
export const requestPasswordResetInputSchema = z.object({
  email: emailSchema,
});

export type RequestPasswordResetInput = z.infer<typeof requestPasswordResetInputSchema>;

// 메일 링크의 토큰으로 새 비밀번호를 설정한다. 비밀번호 규칙은 가입과 동일하다.
export const resetPasswordInputSchema = z.object({
  token: z.string().min(1),
  password: passwordSchema,
});

export type ResetPasswordInput = z.infer<typeof resetPasswordInputSchema>;

// 날짜는 JSON 전송 계약이므로 ISO 8601 문자열로 주고받는다.
// (tRPC 에 transformer 를 쓰지 않으므로 Date 는 직렬화되면 string 이 된다)
export const authUserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string(),
  role: z.enum(USER_ROLES),
  // 타임스탬프에서 파생된 계정 상태. 클라이언트가 인증 안내 화면을 분기하는 데 쓴다.
  status: z.enum(ACCOUNT_STATUSES),
  emailVerifiedAt: z.iso.datetime().nullable(),
});

export type AuthUser = z.infer<typeof authUserSchema>;

export const authTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int().positive(),
});

export type AuthTokens = z.infer<typeof authTokensSchema>;

export const authSessionSchema = z.object({
  user: authUserSchema,
  tokens: authTokensSchema,
});

export type AuthSession = z.infer<typeof authSessionSchema>;

// 소셜 로그인 제공자. URL 경로(:provider)와 프론트 버튼 분기가 공유하는 계약이다.
export const SOCIAL_PROVIDERS = ['naver', 'kakao', 'google'] as const;

export const socialProviderSchema = z.enum(SOCIAL_PROVIDERS);

export type SocialProviderName = z.infer<typeof socialProviderSchema>;

// 소셜 콜백이 발급한 일회용 code 를 세션으로 교환한다. (토큰은 리다이렉트 URL 에 싣지 않는다)
export const socialExchangeInputSchema = z.object({
  code: z.string().min(1),
});

export type SocialExchangeInput = z.infer<typeof socialExchangeInputSchema>;

// 활성 세션(기기 목록) 요약. id 는 토큰 해시라 원문을 역산할 수 없다.
// ipAddress · userAgent 는 수집하지 못했을 수 있으므로 null 을 허용한다.
export const activeSessionSchema = z.object({
  id: z.string(),
  issuedAt: z.iso.datetime(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
});

export type ActiveSession = z.infer<typeof activeSessionSchema>;
